import {
  localAccountKeyError,
  unwrapLocalDocKey,
  wrapLocalDocKey,
} from '@streamlain/view-config/local-data-seal';

import type { LocalAuthBinding } from '../storage/local-data-access';
import {
  localAccountAuthorityLock,
  type LocalAccountKekCustody,
  type LocalAccountKeyRecord,
  LocalAccountKeyStore,
  localAccountLockChannel,
} from './account-key-store';
import type { LocalDocKeyRecord } from './payload';

let custody: LocalAccountKekCustody | null = null;
export function configureLocalDataKeyCustody(value: LocalAccountKekCustody) {
  custody = value;
}

interface CachedKey {
  wrapped: Uint8Array;
  key: CryptoKey;
}
/** Primary account state a doc DB mirror is raised to; mirrors never go down. */
export interface LocalAccountAuthority {
  authEpoch: number;
  logoutPending: boolean;
}
export interface LocalDataKeyOperation {
  authEpoch: number;
  keyId: string;
  cached: ReadonlyMap<string, CachedKey>;
  kek(): Promise<CryptoKey>;
}

/** The connected providers of this realm, for the check after a pause. */
const connected = new Set<LocalDataKeyProvider>();
let recheck: Promise<void> | null = null;
let followUp: Promise<void> | null = null;

/**
 * After a pause (blur, frozen or restored page) a realm may have missed a
 * lock signal: one read of the account record per account locks every
 * connection whose binding moved on. A call during a running check may come
 * after a lock that check's read missed, so all such calls share one more.
 */
export function recheckLocalDataAuthority(): Promise<void> {
  if (recheck)
    return (followUp ??= recheck.then(() => {
      followUp = null;
      return recheckLocalDataAuthority();
    }));
  recheck = (async () => {
    const accounts = new Map<string, LocalDataKeyProvider[]>();
    for (const provider of connected) {
      const providers = accounts.get(provider.binding.accountId) ?? [];
      providers.push(provider);
      accounts.set(provider.binding.accountId, providers);
    }
    await Promise.all(
      [...accounts.values()].map(async providers => {
        const record = await providers[0].readRecord().catch(() => null);
        for (const provider of providers) provider.settle(record);
      })
    );
  })().finally(() => {
    recheck = null;
  });
  return recheck;
}

/** One bounded adapter/worker cache. Custody and the grant seam stay outside storage transactions. */
export class LocalDataKeyProvider {
  private readonly cache = new Map<string, CachedKey>();
  private accountStore: LocalAccountKeyStore | null = null;
  private lockSignal: BroadcastChannel | null = null;
  /** The primary account record as read when the connection opened. */
  private record: LocalAccountKeyRecord | null = null;
  /** A newer epoch was announced: this binding never opens again. */
  private locked = false;
  /** Releases the shared authority lock, held while the connection is unlocked. */
  private releaseAuthority: (() => void) | null = null;
  constructor(readonly binding: LocalAuthBinding) {}
  get cachedKeyCount() {
    return this.cache.size;
  }
  /** Drops held keys; the connection stays bound. */
  clear() {
    this.cache.clear();
    this.accountStore?.close();
  }
  /** Ends the connection: keys, record and lock signal. */
  dispose() {
    connected.delete(this);
    this.clear();
    this.dropAuthority();
    this.record = null;
    this.lockSignal?.close();
    this.lockSignal = null;
  }
  /**
   * Reads the primary account record once for the connection that opens. A
   * record another tab moved on raises this database's mirror and locks the
   * binding: the connection opens, and every request on it fails LOCKED, as
   * a reconnect could not help. From then on the lock lives in memory:
   * logout and login announce a newer epoch on the lock signal, which every
   * connection of the realm hears, and raise the mirrors of every database
   * of the account, which the next connection reads. A realm that may have
   * missed the signal reads the record again (recheckLocalDataAuthority).
   */
  async connect(
    raise: (authority: LocalAccountAuthority) => Promise<void>,
    superseded = false
  ) {
    this.listen();
    connected.add(this);
    // Each connection decides from its own read: a stale binding reads a
    // record that moved on and locks again.
    this.locked = false;
    this.record = null;
    // Held before the read: a logout either waits for this connection or
    // ran before it and is in the record.
    await this.holdAuthority();
    const record = await this.store().read();
    if (record && this.current(record) && !superseded) {
      this.record = record;
      return;
    }
    this.lock();
    if (record)
      await raise({
        authEpoch: record.authEpoch,
        logoutPending: record.logoutPending,
      });
  }
  /** Every storage request: the connection is open and no newer epoch was announced. */
  get authorized() {
    return !this.locked && !!this.record;
  }
  assertAuthority() {
    if (!this.authorized) {
      this.clear();
      throw localAccountKeyError('LOCKED');
    }
  }
  /** The primary account record as stored now. */
  readRecord() {
    return this.store().read();
  }
  /** Locks the connection unless `record` still carries its binding. */
  settle(record: LocalAccountKeyRecord | null) {
    if (this.locked || !this.record) return;
    if (!record || !this.current(record)) this.lock();
  }
  private current(record: LocalAccountKeyRecord) {
    return (
      record.accountId === this.binding.accountId &&
      record.authEpoch === this.binding.authEpoch &&
      !record.logoutPending
    );
  }
  private listen() {
    if (this.lockSignal) return;
    this.lockSignal = new BroadcastChannel(
      localAccountLockChannel(this.binding.accountId)
    );
    this.lockSignal.onmessage = event => {
      const epoch = (event.data as { authEpoch?: unknown } | null)?.authEpoch;
      if (typeof epoch === 'number') {
        if (epoch > this.binding.authEpoch) this.lock();
        return;
      }
      // A message without an epoch decides through the record itself.
      this.clear();
      void this.store()
        .read()
        .then(
          record => {
            if (!record || !this.current(record)) this.lock();
          },
          () => this.lock()
        );
    };
  }
  private lock() {
    this.locked = true;
    this.clear();
    this.dropAuthority();
  }
  /** Resolves once the shared authority lock is granted; a drop before that
   * releases it on grant. */
  private holdAuthority() {
    this.dropAuthority();
    let release!: () => void;
    const held = new Promise<void>(resolve => {
      release = resolve;
    });
    this.releaseAuthority = release;
    return new Promise<void>((granted, refused) => {
      navigator.locks
        .request(
          localAccountAuthorityLock(this.binding.accountId),
          { mode: 'shared' },
          () => {
            granted();
            return held;
          }
        )
        .catch(refused);
    });
  }
  private dropAuthority() {
    this.releaseAuthority?.();
    this.releaseAuthority = null;
  }
  private store() {
    if (!custody) throw localAccountKeyError('KEY_UNAVAILABLE');
    return (this.accountStore ??= new LocalAccountKeyStore({
      accountId: this.binding.accountId,
      kekCustody: custody,
    }));
  }
  /**
   * The record of the connection, read again only while it cannot serve a
   * keyed operation: before the first key install (installKeys sets keyId
   * without a new epoch) or, with record custody, before restoreKek put the
   * KEK back after a login.
   */
  private async keyedRecord(): Promise<LocalAccountKeyRecord> {
    this.assertAuthority();
    const record = this.record!;
    if (
      record.keyId &&
      record.envelope &&
      (custody?.kind !== 'record' || record.kek)
    )
      return record;
    const next = await this.store().read();
    if (!next || !this.current(next)) {
      this.lock();
      throw localAccountKeyError('LOCKED');
    }
    this.record = next;
    return next;
  }
  async beginOperation(): Promise<LocalDataKeyOperation> {
    const record = await this.keyedRecord();
    if (!record.keyId || !record.envelope) {
      this.clear();
      throw localAccountKeyError('KEY_UNAVAILABLE');
    }
    let kek: Promise<CryptoKey> | null = null;
    return {
      authEpoch: record.authEpoch,
      keyId: record.keyId,
      cached: new Map(this.cache),
      kek: () =>
        (kek ??= this.store()
          .readKek(this.binding, record)
          .catch(error => {
            this.clear();
            throw error;
          })),
    };
  }

  /** The connection's authority must still match after crypto, before output/commit. */
  async assertOperation(operation: LocalDataKeyOperation | null) {
    if (!operation) {
      this.clear();
      throw localAccountKeyError('LOCKED');
    }
    this.assertAuthority();
    const record = this.record!;
    if (record.authEpoch !== operation.authEpoch) {
      this.clear();
      throw localAccountKeyError('LOCKED');
    }
    if (!record.keyId || !record.envelope || record.keyId !== operation.keyId) {
      this.clear();
      throw localAccountKeyError('KEY_UNAVAILABLE');
    }
  }

  async assertAvailable(expectedKeyId?: string) {
    const operation = await this.beginOperation();
    this.assertKeyId(operation, expectedKeyId);
    return operation.keyId;
  }
  private assertKeyId(
    operation: LocalDataKeyOperation,
    expectedKeyId?: string
  ) {
    if (expectedKeyId && expectedKeyId !== operation.keyId) {
      this.clear();
      throw localAccountKeyError('KEY_UNAVAILABLE');
    }
  }
  async candidate(
    keyScopeId: string,
    operation:
      | LocalDataKeyOperation
      | Promise<LocalDataKeyOperation> = this.beginOperation()
  ): Promise<LocalDocKeyRecord> {
    const current = await operation;
    const keyId = current.keyId;
    const wrappedDEK = await wrapLocalDocKey(await current.kek());
    return {
      version: 1,
      accountId: this.binding.accountId,
      workspaceId: this.binding.workspaceId,
      keyScopeId,
      keyId,
      wrappedDEK,
      registryRevision: crypto.randomUUID(),
    };
  }
  async getKey(
    record: LocalDocKeyRecord,
    expectedScopeId: string,
    committed: boolean,
    operation:
      | LocalDataKeyOperation
      | Promise<LocalDataKeyOperation> = this.beginOperation()
  ) {
    if (
      !record ||
      record.version !== 1 ||
      record.accountId !== this.binding.accountId ||
      record.workspaceId !== this.binding.workspaceId ||
      record.keyScopeId !== expectedScopeId ||
      !record.registryRevision ||
      !(record.wrappedDEK instanceof Uint8Array) ||
      record.wrappedDEK.length !== 40
    )
      throw localAccountKeyError('CORRUPT');
    const current = await operation;
    this.assertKeyId(current, record.keyId);
    const cacheId = this.cacheId(record);
    const found = current.cached.get(cacheId);
    if (
      found &&
      found.wrapped.length === record.wrappedDEK.length &&
      found.wrapped.every((byte, index) => byte === record.wrappedDEK[index])
    ) {
      if (committed) this.remember(record, found.key);
      return { keyId: record.keyId, key: found.key };
    }
    const key = await unwrapLocalDocKey(await current.kek(), record.wrappedDEK);
    if (committed) this.remember(record, key);
    return { keyId: record.keyId, key };
  }
  private cacheId(record: LocalDocKeyRecord) {
    return JSON.stringify([
      record.accountId,
      record.workspaceId,
      record.keyScopeId,
      record.keyId,
      this.binding.authEpoch,
      record.registryRevision,
    ]);
  }
  remember(record: LocalDocKeyRecord, key: CryptoKey) {
    const id = this.cacheId(record);
    this.cache.delete(id);
    this.cache.set(id, { key, wrapped: new Uint8Array(record.wrappedDEK) });
    if (this.cache.size > 64)
      this.cache.delete(this.cache.keys().next().value!);
  }
}
